/**
 * Billing seam — start an SSLCommerz checkout. In mock mode it short-circuits to
 * the return page; against the real API it hits /payments/sslcommerz/init and the
 * caller redirects the browser to the returned hosted-checkout URL.
 */
import { useQuery } from '@tanstack/react-query';
import { USE_MOCKS, apiGet, apiPost, apiDelete, mockDelay } from './client';

export interface CheckoutSession {
  gatewayPageURL: string;
  tranId: string;
}

export function startCheckout(input: { businessId: string; priceId: string }): Promise<CheckoutSession> {
  if (USE_MOCKS) {
    return mockDelay({ gatewayPageURL: '/billing/return?status=success&tran=mock', tranId: 'mock' });
  }
  return apiPost<CheckoutSession>('/payments/sslcommerz/init', input);
}

/**
 * Renew a lapsed subscription at its existing price (suspended→pay). The server
 * resolves the renewal price from the business's current subscription, so the
 * client only needs the businessId; caller redirects to the returned hosted URL.
 */
export function renewSubscription(businessId: string): Promise<CheckoutSession> {
  if (USE_MOCKS) {
    return mockDelay({ gatewayPageURL: '/billing/return?status=success&tran=mock-renew', tranId: 'mock-renew' });
  }
  return apiPost<CheckoutSession>('/payments/sslcommerz/renew', { businessId });
}

/**
 * Self-serve upgrade: charges the DIFFERENCE for the days left in the paid period (server
 * billing/proration.ts) through SSLCommerz; the caller redirects to the returned hosted URL and the
 * plan changes once payment succeeds. Under ৳10 owed there is nothing to charge — the server applies
 * the upgrade at once and answers `applied: true` with no URL.
 */
export function startUpgradeCheckout(businessId: string, priceId: string): Promise<CheckoutSession & { applied?: boolean }> {
  if (USE_MOCKS) {
    return mockDelay({ gatewayPageURL: '/billing/return?status=success&tran=mock-upgrade', tranId: 'mock-upgrade' });
  }
  return apiPost<CheckoutSession>(`/businesses/${businessId}/subscription/upgrade`, { priceId });
}

/** Self-serve downgrade: no charge, takes effect at the next renewal. */
export function scheduleDowngrade(businessId: string, priceId: string): Promise<{ ok: true; pendingPlanChange: { packageName: string; unitAmountMinor: number; interval: string } }> {
  if (USE_MOCKS) {
    return mockDelay({ ok: true as const, pendingPlanChange: { packageName: 'Starter', unitAmountMinor: 30000, interval: 'year' } });
  }
  return apiPost(`/businesses/${businessId}/subscription/downgrade`, { priceId });
}

/** Cancel a scheduled downgrade before it takes effect. */
export function cancelScheduledDowngrade(businessId: string): Promise<{ ok: true }> {
  if (USE_MOCKS) return mockDelay({ ok: true as const });
  return apiDelete(`/businesses/${businessId}/subscription/downgrade`);
}

/**
 * Pay one or more of a business's OWN open invoices in a single SSLCommerz
 * transaction. All ids must belong to the SAME business; the callback settles
 * those existing invoices (no new invoice minted). Caller redirects to the
 * returned hosted-checkout URL.
 */
export function payInvoices(input: { businessId: string; invoiceIds: string[] }): Promise<CheckoutSession> {
  if (USE_MOCKS) {
    return mockDelay({ gatewayPageURL: '/billing/return?status=success&tran=mock-inv', tranId: 'mock-inv' });
  }
  return apiPost<CheckoutSession>('/payments/sslcommerz/pay-invoices', input);
}

/**
 * Record an off-gateway payment (cash / bKash / Nagad / bank transfer) against a
 * business. Extends the subscription, writes a paid invoice, and — for reseller-
 * collected money — the correct reseller_ledger entry. Server-enforced to ITCity
 * staff / resellers. Used by the reseller CustomerDetail + admin BusinessDetail modals.
 */
export type CashMethod = 'manual_cash' | 'bkash' | 'nagad' | 'bank_transfer';
export const CASH_METHOD_LABEL: Record<CashMethod, string> = {
  manual_cash: 'Cash',
  bkash: 'bKash',
  nagad: 'Nagad',
  bank_transfer: 'Bank transfer',
};
export type RecordCashResult = {
  paymentId: string;
  invoiceNo: string;
  status: string;
  currentPeriodEnd: string | null;
  /** true = the money cleared what was owed and service is active. false = a part-payment on an
   *  open invoice, or advance cash short of one billing period kept as credit (optional so an
   *  older API that omits it reads as the old always-extended behaviour). */
  extended?: boolean;
  /** How much was kept as credit (minor units) — only for advance cash short of a renewal. */
  creditMinor?: number;
  /** Set when this entry STARTED a subscription on a named plan (the business had no live one),
   *  e.g. "Basic (yearly)". */
  startedPlan?: string | null;
};
export const recordCashPayment = (input: {
  businessId: string;
  amountMinor: number;
  method?: CashMethod;
  note?: string;
  receiptRef?: string;
  /** Required together when the business has NO live subscription: the plan this payment starts.
   *  The API refuses (409, code PLAN_REQUIRED) without them, and refuses them when a live
   *  subscription exists — plan changes go through the override / upgrade paths. */
  packageId?: string;
  interval?: 'month' | 'year';
}) =>
  USE_MOCKS
    ? mockDelay<RecordCashResult>({ paymentId: 'pay_mock', invoiceNo: 'INV-MOCK-0001', status: 'active', currentPeriodEnd: null, extended: true, creditMinor: 0 })
    : apiPost<RecordCashResult>('/payments/cash', input);

/* ── Customer account ledger (invoices + payments + running balance) ─────────── */
export type LedgerRow = {
  kind: 'invoice' | 'payment';
  date: string | null;
  ref: string;
  description: string;
  debitMinor: number;
  creditMinor: number;
  balanceMinor: number;
  status: string;
  method: string | null;
};
export type BusinessLedger = {
  businessId: string;
  name: string;
  summary: { openingBalanceMinor: number; totalInvoicedMinor: number; totalPaidMinor: number; balanceDueMinor: number };
  rows: LedgerRow[];
};
const MOCK_LEDGER: BusinessLedger = {
  businessId: 'mock', name: 'Customer',
  summary: { openingBalanceMinor: 0, totalInvoicedMinor: 0, totalPaidMinor: 0, balanceDueMinor: 0 },
  rows: [],
};
export const useResellerCustomerLedger = (id: string) =>
  useQuery({
    queryKey: ['reseller', 'ledger', id],
    queryFn: () => (USE_MOCKS ? mockDelay(MOCK_LEDGER) : apiGet<BusinessLedger>(`/reseller/customers/${id}/ledger`)),
    enabled: !!id,
  });
export const useAdminBusinessLedger = (id: string) =>
  useQuery({
    queryKey: ['admin', 'ledger', id],
    queryFn: () => (USE_MOCKS ? mockDelay(MOCK_LEDGER) : apiGet<BusinessLedger>(`/admin/businesses/${id}/ledger`)),
    enabled: !!id,
  });
